export type FormatNumberOptions = {
  decimals?: number;
  fallback?: string;
  useGrouping?: boolean;
};

export function formatNumber(
  value: number | string | null | undefined,
  options: FormatNumberOptions = {},
): string {
  const { decimals = 2, fallback = "-", useGrouping = true } = options;

  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const numericValue = typeof value === "number" ? value : Number(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  }).format(numericValue);
}

export const numberWithCommas = (
  value: number | string | null | undefined,
) => formatNumber(value, { decimals: 0 });

export type NumericFontSizes = {
  base: string;
  mid: string;
  small: string;
  tiny: string;
};

const DEFAULT_NUMERIC_FONT_SIZES: NumericFontSizes = {
  base: "1.5rem",
  mid: "1.2rem",
  small: "1rem",
  tiny: "0.85rem",
};

/**
 * Pick a font-size that keeps a (possibly very long) numeric/currency string
 * from overflowing its card WITHOUT truncating it. Deterministic — based purely
 * on the rendered string length, so e.g. "฿10,230,000" stays large while
 * "฿230,787,830" steps down to fit. Use as the `fontSize` of the value text.
 */
export function numericFontSize(
  text: string,
  sizes: Partial<NumericFontSizes> = {},
): string {
  const s = { ...DEFAULT_NUMERIC_FONT_SIZES, ...sizes };
  const len = text.length;
  if (len <= 8) return s.base;
  if (len <= 11) return s.mid;
  if (len <= 14) return s.small;
  return s.tiny;
}
